// KYT v2 · the small pieces a payment row and its assessment share (UI rebuild batch 4; the KYT lane's since 27 Oct 2026).
//
// Information first, in the order the operator reads a payment: WHO paid, HOW MUCH, INTO WHICH wallet, WHAT the
// screening found (attribution, sources, list status) — and only then the lane's level word, as a quiet tag. No verdict
// column, no green: a level is the worker's seat for the payment under HIS alert lines, said in the lane's own word.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { KytIncoming, KytIncomingWallet, KytPayment } from '../../../lib/api';
import type { KytBand } from '../../../lib/api';
import { dateBucket, dubaiDate, fmtDay, fmtTime } from '../../../lib/format';
import { BAND_TONE, dollars, mainRisk, seatOf, share, whole, windowCounts, type SenderName } from '../../../screens/Kyt';
import { Pill, type PillTone } from '../../Pill';

/** v2 draws no green on KYT: a level, an answer or a band is information, never a clean bill (the design system: something
 *  not fully checked is never green; the operator, 8 Oct 2026: "decision will be manual"). The WORD stays the lane's; only
 *  its colour is dropped. */
export const quiet = (t: PillTone): PillTone => (t === 'good' ? 'neutral' : t);

/** The screener's band for a wallet, as a word: "no score" when a provider looked and gave none, "unscreened" when nobody
 *  looked (two different facts, two words) — and never green. */
export function BandTag({ band, looked = false }: { band: KytBand | string | null | undefined; looked?: boolean }) {
  if (!band) return <Pill>{looked ? 'no score' : 'unscreened'}</Pill>;
  return <Pill tone={quiet(BAND_TONE[band as KytBand] ?? 'neutral')}>{band}</Pill>;
}

/** Whose payment this is: the party it is booked to in the Books — the deal the wallet system's deposit settles, else
 *  the Books' chain match (the operator, 28 Oct 2026: "Unknown sender" says nothing; if anything, "the linked party
 *  with whom this payment got booked", decided for the new wallet system, where every deposit address is issued to a
 *  party). Nothing when nothing is booked: the row leads with the time. The chat's claim and GL's name for the sender
 *  are screening facts, said in the assessment (an adverse claim is the row's main risk, lib mainRisk), never the party. */
export const bookedTo = (p: KytPayment): { name: string; via: 'deal' | 'booking' } | null => (p.party ? { name: p.party.name, via: p.party.via } : null);

export function Who({ p }: { p: KytPayment }) {
  const b = bookedTo(p);
  return b ? <span className="break-words">{b.name}</span> : null;
}

/** Who sent it, for the assessment: the onboarded customer whose chat named this wallet (a blocked chat or a declined
 *  applicant in a WORD, never by colour alone), else the name GL gives the sending address, else nothing — the address
 *  under it is the sender then. */
export function senderWords(p: KytPayment, gl: SenderName | null): { name: string; tone: 'warn' | null } | null {
  if (p.customer) {
    if (p.standing === 'blocked') return { name: `Blocked chat · ${p.customer}`, tone: 'warn' };
    if (p.standing === 'declined') return { name: `Declined applicant · ${p.customer}`, tone: 'warn' };
    return { name: p.customer, tone: null };
  }
  if (gl) return { name: gl.name, tone: null };
  return null;
}

export function Sender({ p, gl }: { p: KytPayment; gl: SenderName | null }) {
  const w = senderWords(p, gl);
  return w ? <span className={`block break-words ${w.tone === 'warn' ? 'text-warn' : ''}`}>{w.name}</span> : null;
}

const FINDING_TEXT = { bad: 'text-neg', warn: 'text-warn', good: 'text-tg-text', hint: 'text-tg-text' } as const;

/** What the screening found: the lane's mainRisk — the label the payment was RATED on, then its share and who. A clean
 *  payment's line names where most of it came from, in plain text (never green). */
export function Finding({ p, compact = false, table = false }: { p: KytPayment; compact?: boolean; table?: boolean }) {
  const r = mainRisk(p);
  // A look-alike of an address the desk knows (address poisoning, 3 Nov 2026): a red line UNDER the finding, in every tier
  // (the From column is the full tier's only) — the screening's own words stay, an alert's above all (Astra on b91ac09d).
  const decoy = p.poisoning && <span className="block break-words text-meta text-neg">Look-alike — address poisoning · of {p.poisoning.of} · never a payee</span>;
  // In a table the share and its dollars have a column of their own (Exposure) and "deep checked" is the sheet's: the
  // label, and who — or the line under it when no exposure decided the risk (a list's name, GL's score).
  const sub = table ? (r.pct != null ? r.who ?? '' : r.sub ?? '') : [r.sub, p.deep && 'deep checked'].filter(Boolean).join(' · ');
  if (compact) {
    return (
      // A phone card's meta line is truncated elsewhere; this one wraps whole, so an adverse owner's name is never cut.
      <span className="block"><span className={`block whitespace-normal break-words ${FINDING_TEXT[r.tone]}`}>{r.label}{sub && <span className="text-tg-hint"> · {sub}</span>}</span>{decoy}</span>
    );
  }
  return (
    <span className="block min-w-0">
      <span className={`block break-words ${FINDING_TEXT[r.tone]}`}>{r.label}</span>
      {sub && <span className="block break-words text-meta text-tg-hint">{sub}</span>}
      {decoy}
    </span>
  );
}

/** The lane's level word, as a tag after the information — never a column of its own, never green. */
export function LevelTag({ p }: { p: KytPayment }) {
  const s = seatOf(p);
  return <Pill tone={quiet(s.tone)}>{s.word}</Pill>;
}

/** The kit's DetailSheet closes on Escape; this hands focus to its Close button when it opens and back to what opened it
 *  when it closes (Astra's check: "dialogs close with Escape and return focus"). */
export function useSheetFocus(open: boolean) {
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const t = window.setTimeout(() => {
      document.querySelector<HTMLButtonElement>('aside[role=dialog] button[aria-label="Close"]')?.focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      const el = opener.current;
      if (el?.isConnected) el.focus();
    };
  }, [open]);
}

// ── The table's own cells and groupings (28 Oct 2026, the operator: "no tabular format … no datewise view") ──

/** The time alone, Dubai's clock: the rows stand under a day header, so a row never says its date or "Y". */
export const Time = ({ p }: { p: KytPayment }) => <span className="font-mono tabular">{p.at ? fmtTime(p.at) : '—'}</span>;

// ── ONE FACT A CELL (1 Nov 2026, his "Amount, chain, date and time everything in 1st column — doesn't it look wrong"): the
// share that decided a row, its dollars of this payment and the GL score it was rated on are three cells, never one line.

const Dash = () => <span className="font-mono tabular text-tg-hint">—</span>;
/** The share of the payment that decided its risk; "—" when no share did. */
export function Share({ p }: { p: KytPayment }) {
  const r = mainRisk(p);
  return r.pct == null ? <Dash /> : <span className="whitespace-nowrap font-mono tabular">{share(r.pct)}</span>;
}
/** That share in dollars of this payment — the figure the rules weigh; "—" when there is none. */
export function Exposed({ p }: { p: KytPayment }) {
  const r = mainRisk(p);
  return r.usd == null ? <Dash /> : <span className="whitespace-nowrap font-mono tabular">{dollars(r.usd)}</span>;
}
/** GL's score of the payment (0–100); "—" when GL gave none. The band is the assessment's. */
export function GlScore({ p }: { p: KytPayment }) {
  return p.score == null ? <Dash /> : <span className="font-mono tabular">{whole(Math.round(p.score))}</span>;
}

/** A phone card's labelled line — what the table shows as a column, the card shows as a line with its label (UI rebuild's
 *  audit, 1 Nov 2026: "the phone loses facts the laptop has"). */
export const CardLine = ({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) => (
  <span className={`block whitespace-normal ${className}`}><span className="text-tg-hint">{label}</span> <span className="text-tg-text">{children}</span></span>
);

/** Is the window at least this wide? The rebuilt tables choose their columns by it: the full set needs the room of a
 *  1,440 window (the 9-digit amount, the sender, the share, its dollars and the score each in a cell), a 1,024 one holds
 *  the core — nothing is cut, a dropped column's fact is the assessment's (1 Nov 2026, the audit at 1024). Rendered
 *  without a window (the tests) it is true: the full set is what they read. */
export function useMinWidth(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const ask = () => (typeof window === 'undefined' || typeof window.matchMedia !== 'function' ? true : window.matchMedia(query).matches);
  const [wide, setWide] = useState(ask);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const m = window.matchMedia(query);
    const on = () => setWide(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return wide;
}
/** The three widths a rebuilt KYT table is drawn at: `full` from a 1,400 px window, `mid` from 1,200, `core` below. */
export type Tier = 'full' | 'mid' | 'core';
export function useTier(): Tier {
  // 1440: where the kit draws this register's table with every seat (tableFromColumns, UI rebuild 1 Nov 2026); a 1400 start
  // left 1400–1439 with the full tier's columns and the kit's cards.
  const full = useMinWidth(1440);
  const mid = useMinWidth(1200);
  return full ? 'full' : mid ? 'mid' : 'core';
}

/** A wallet's label in a cell (P-075, 29 Oct 2026): the Books name a desk wallet "Hot wallet · TRON (October 2026)", and
 *  at 15% of a 1440 px screen the month — the only part that tells two TRON wallets apart — was the part cut. In a cell
 *  the type loses its "wallet" and the month its long name: "Hot · TRON (Oct 2026)"; the whole label is the cell's title.
 *  A label of another shape is left as it is. */
export function compactWallet(label: string): string {
  const parts = label.trim().split(' · ');
  if (parts.length < 2) return label.trim();
  const type = parts[0]!.replace(/\s+wallet$/i, '');
  const rest = parts.slice(1).join(' · ').replace(/\((January|February|March|April|May|June|July|August|September|October|November|December) (\d{4})\)$/, (_m, month: string, year: string) => `(${month.slice(0, 3)} ${year})`);
  return `${type || parts[0]} · ${rest}`;
}

export type DayGroup<T> = { key: string; label: string; rows: T[] };
/** The rows under Dubai-day headers, newest day first, each day's rows in the order given: "Today · 28 Oct", "Yesterday
 *  · 27 Oct", then the day alone. A row with no time stands last, under "No time". */
export function byDubaiDay<T extends { at: string | null }>(rows: readonly T[]): DayGroup<T>[] {
  const groups = new Map<string, DayGroup<T>>();
  for (const r of rows) {
    const key = r.at ? dubaiDate(r.at) : '';
    let g = groups.get(key);
    if (!g) {
      const bucket = r.at ? dateBucket(r.at) : null;
      const day = r.at ? fmtDay(r.at) : 'No time';
      g = { key, label: bucket === 'today' ? `Today · ${day}` : bucket === 'yesterday' ? `Yesterday · ${day}` : day, rows: [] };
      groups.set(key, g);
    }
    g.rows.push(r);
  }
  return [...groups.values()].sort((a, b) => (a.key === '' ? 1 : b.key === '' ? -1 : b.key.localeCompare(a.key)));
}

/** The strip of four figures over the Payments and Wallets views (the kit's SummaryStrip), for the chosen window: it
 *  replaces the banner sentence and the "Not fully checked:" line (28 Oct 2026). A figure above zero takes its tone. */
export function stripItems(inc: KytIncoming, pick: (w: KytIncomingWallet) => boolean = () => true): Array<{ label: string; value: ReactNode; sub?: ReactNode }> {
  const c = windowCounts(inc, pick);
  const n = inc.wallets.filter(pick).length;
  const fig = (n: number, tone: 'bad' | 'warn'): ReactNode => <span className={n > 0 ? (tone === 'bad' ? 'text-neg' : 'text-warn') : ''}>{whole(n)}</span>;
  return [
    { label: 'Alerts', value: fig(c.alerts.n, 'bad'), sub: c.alerts.n > 0 ? dollars(c.alerts.usd) : undefined },
    { label: 'Watch', value: fig(c.watch.n, 'warn'), sub: c.watch.n > 0 ? dollars(c.watch.usd) : undefined },
    { label: 'Not checked', value: fig(c.unchecked, 'warn'), sub: c.incomplete > 0 ? `${whole(c.incomplete)} in part` : undefined },
    { label: 'Wallets not read', value: fig(c.unread, 'warn'), sub: `of ${whole(n)}` },
  ];
}
